import {_} from 'lodash';
import * as ko from "knockout";
import {IDialogOptionsComponent} from "../../component/dialog/index";
import {IMessageData, IOrganization, IPromiseRequest, ISearchParams} from "../../index";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {ButtonManager} from "../../util/ButtonManager";
import {MAX_RESULTS_PER_PAGE, ObjectTypeId} from "../../constants";
import PromiseRequest from "../../req/PromiseRequest";
import {getCancelButton, getContinueButton} from "../../constants/RouteDefinitions";
import {REQUEST_MESSAGE, UPDATING_MESSAGE} from "../../constants/Language";
import {createErrorMessage, isPartOf} from "../../util/misc";
import {nsLanguage} from "../../util/nsLanguage";
import {global} from "../../appContext";
import {nsClone} from "../fn";
import targetOrg = nsClone.targetOrg;

export interface IMultiParams<T> {
    selectedObjects: T[]
    dialog: IDialogOptionsComponent
    requestParams: ISearchParams
}

export enum Step {
    Init,
    Introduction,
    Process,
    Success,
    Error,
}

export abstract class AbstractMulti<T> {

    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker()
    protected abstract objectTypeId: ObjectTypeId
    protected abstract req_fetch: PromiseRequest
    protected arrError!: { item: T, error: string }[]
    protected arrSuccess!: T[]
    protected introMessage!: string
    protected readonly DUPLICATE_MESSAGE: string = `source and target same result duplicate`;
    protected readonly Step = Step
    protected readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null)
    protected readonly organizations = global.login.organizations
    protected readonly pageButtons: ButtonManager = new ButtonManager()
    protected readonly srcOrg = global.login.selectedOrganization
    protected readonly step: KnockoutObservable<Step | null> = ko.observable(null)
    protected readonly trgOrg: KnockoutObservable<IOrganization> = targetOrg
    protected requests!: IPromiseRequest[]
    protected selectedObjects: T[];

    constructor(protected params: IMultiParams<T>) {
        this.kosub.subscribe(this.step as KnockoutObservable<Step>, this.onStepChange.bind(this))
        this.selectedObjects = params.selectedObjects

        this.initButtons()

        requestAnimationFrame(() => {
            if (!this.selectedObjects?.length) {
                this.step(Step.Init)
            } else {
                this.step(Step.Introduction)
            }
        })
    }

    protected initButtons() {
        this.pageButtons.add(getCancelButton())

        this.pageButtons.add({
            onClick: () => this.params.dialog.doClose?.(),
            label: () => 'cancel',
            icon: () => 'fa-ban',
            cssClass: 'action',
            validator: () => !this.isStep(Step.Success, Step.Error),
        })

        this.pageButtons.add({
            onClick: () => this.params.dialog.doClose?.(),
            label: 'close',
            icon: 'fa-times',
            cssClass: 'action',
            validator: () => this.isStep(Step.Success, Step.Error),
        })

        this.pageButtons.add(getContinueButton({
            onClick: () => this.step(Step.Process),
            label: 'yes, continue',
            validator: () => this.isStep(Step.Introduction),
        }))
    }

    protected onStepChange(step: Step) {
        switch (step) {
            case Step.Init:
                this.messageData({message: REQUEST_MESSAGE})
                this.req_fetch
                    .request(_.defaultsDeep({resultsprpage: MAX_RESULTS_PER_PAGE}, this.params.requestParams))
                    .then((data: T[]) => {
                        this.selectedObjects = data
                        this.messageData(null)
                        this.step(Step.Introduction)
                    })
                    .catch((reason: string) => {
                        this.messageData(createErrorMessage(String(reason)))
                        this.step(Step.Error)
                    })
                break;
            case Step.Introduction:
                break;
            case Step.Process:
                this.params.dialog.isClosable?.(false)
                this.messageData({message: UPDATING_MESSAGE})

                this.requests?.forEach(o => o.abort())
                this.requests = []
                this.arrSuccess = []
                this.arrError = []

                const fnArr = this.getPromiseArray()

                this.doNext(fnArr)
                    .then(() => this.onPromiseArraySuccess())
                    .catch((reason: string) => {
                        this.step(Step.Error)
                        this.messageData(createErrorMessage(String(reason)))
                        this.params.dialog.onError?.()
                    })
                break;
            case Step.Success:
                break;
            case Step.Error:
                break;

        }
    }

    protected abstract getPromiseArray(): (() => Promise<any>)[]

    protected doNext(fnArr: (() => Promise<T>)[]): Promise<T | undefined> {
        if (!fnArr.length) {
            return Promise.resolve(undefined)
        }

        const fn = fnArr.shift()!

        return fn().then(() => {
            if (fnArr.length) {
                return this.doNext(fnArr)
            }
        })
    }

    protected abstract onPromiseArraySuccess(): void

    protected isStep = isPartOf.bind(this, this.step)

    protected getNumObjectsText(arr: any[]): string {
        return nsLanguage.getNumObjectsLabel(arr.length, this.objectTypeId, false)
    }

    dispose() {
        this.kosub.unsuscribeAll()
        this.req_fetch.abort()
        this.requests?.forEach((o) => o.abort())
    }
}
